// `scan` — barcode / QR (docs/plans/transaction-wizard.md §6.4). Three ways in,
// one path: the camera (the shared ScanCapture), an always-listening
// keyboard-wedge field (a hardware scanner types the code and sends Enter), and
// typing it by hand. The SERVER parses the text with the step's own rules
// (`wizard_scan`); a good scan beeps and moves on (or, in a loop, stays for the
// next one), a rejected one buzzes and changes nothing. The last scans are
// listed with Undo last.
//
// A wedge scanner can fire the next label before the server answered the
// last: scans are QUEUED and processed in order (`ctl.enqueueScan`), never
// dropped. In a loop the camera stays open (`continuous`) and flashes each
// result; a single scan closes it after one read.

import { useEffect, useRef, useState } from 'react'
import { Camera, CheckCircle2, Keyboard, ScanLine, Undo2, XCircle } from 'lucide-react'
import { ScanCapture } from '../../scan/ScanCapture'
import { text } from '../model'
import { BAD, GOOD, T, button, input as inputStyle, muted } from '../styles'
import { tw } from '../../testHooks'
import type { StepProps } from './common'

export default function ScanStep({ ctl, handheld }: StepProps) {
  const { step, scope, ui, busy } = ctl
  const [value, setValue] = useState('')
  const [camera, setCamera] = useState(false)
  const [queued, setQueued] = useState(0)
  const wedge = useRef<HTMLInputElement>(null)

  // Keep the wedge listening: refocus it whenever nothing else holds focus —
  // also while a scan is being processed, so the next one is not lost.
  useEffect(() => {
    if (camera) return
    const focus = () => {
      const a = typeof document !== 'undefined' ? document.activeElement : null
      const typing = a && a !== wedge.current && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA' || a.tagName === 'SELECT'
        || (a as HTMLElement).isContentEditable)
      if (!typing) wedge.current?.focus()
    }
    focus()
    const id = setInterval(focus, 800)
    return () => clearInterval(id)
  }, [camera, busy, ui.key])

  // A new step (or the same one entered again) starts with the camera closed.
  useEffect(() => { setCamera(false) }, [ui.key])

  if (!step || !scope) return null
  const submit = async (raw: string): Promise<boolean> => {
    const t = raw.trim()
    if (!t) return false
    setValue('')
    setQueued(n => n + 1)
    try {
      return await ctl.enqueueScan(t)
    } finally {
      setQueued(n => Math.max(0, n - 1))
    }
  }
  const prompt = step.prompt ? text(step.prompt, scope) : 'Scan the code'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: handheld ? '18px 14px' : '14px',
        border: `2px dashed ${T.border}`, borderRadius: 10, background: T.band,
      }}>
        <ScanLine size={handheld ? 32 : 24} style={{ color: T.accent, flexShrink: 0 }} />
        <div style={{ fontSize: handheld ? 18 : 15, fontWeight: 600 }}>{prompt}</div>
      </div>
      <form onSubmit={e => { e.preventDefault(); void submit(value) }}
        style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Keyboard size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: T.muted }} />
          <input ref={wedge} value={value} onChange={e => setValue(e.target.value)}
            // Enter submits (a wedge scanner's terminator) — stop it from
            // also pressing the wizard's Next.
            onKeyDown={e => { if (e.key === 'Enter') e.stopPropagation() }}
            placeholder={step.manual_entry === false ? 'Waiting for the scanner…' : 'Scan, or type the code and press Enter'}
            autoComplete="off" autoCapitalize="off" spellCheck={false} inputMode={step.manual_entry === false ? 'none' : 'text'}
            style={{ ...inputStyle(handheld), paddingLeft: 32, fontFamily: T.mono }}
            {...tw('wizard-scan-input', step.id)} aria-label={prompt} />
        </div>
        <button type="button" style={button(handheld)} onClick={() => setCamera(true)}
          title="Use the camera" {...tw('action', 'camera')}>
          <Camera size={18} />{handheld ? '' : ' Camera'}
        </button>
      </form>
      {queued > 1 && (
        <div style={muted} role="status">{queued - 1} more scan{queued > 2 ? 's' : ''} waiting…</div>
      )}
      {ui.scanLog.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ ...muted, flex: 1 }}>Last scans</span>
            {ui.scanLog.some(s => s.ok) && (
              <button type="button" style={{ ...button(false), minHeight: 30, fontSize: 13 }} onClick={() => ctl.undoScan()}
                {...tw('action', 'undo_scan')}>
                <Undo2 size={14} /> Undo last
              </button>
            )}
          </div>
          {ui.scanLog.map(s => (
            <div key={s.id} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 6, fontSize: 14,
              background: s.ok ? GOOD.bg : BAD.bg, color: s.ok ? GOOD.fg : BAD.fg,
            }}>
              {s.ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
              <code style={{ fontFamily: T.mono }}>{s.text}</code>
              <span style={{ flex: 1, textAlign: 'right' }}>{s.message}</span>
            </div>
          ))}
        </div>
      )}
      {camera && (step.loop ? (
        // A loop: the camera stays open; each new code is queued and its
        // outcome flashed. Done (or the step moving on) closes it.
        <ScanCapture continuous title={prompt}
          onScan={async (t) => {
            const good = await submit(t)
            return { ok: good, message: good ? undefined : ctl.lastScan()?.message }
          }}
          onClose={() => setCamera(false)}
        />
      ) : (
        <ScanCapture
          onScan={(t) => { setCamera(false); void submit(t) }}
          onClose={() => setCamera(false)}
        />
      ))}
    </div>
  )
}
